import { useMemo } from 'react';
import { SmilePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useReactions } from '../../hooks/useReactions';
import { formatCompactNumber } from '../../utils/format';
import { REACTIONS, REACTION_FIELD_MAP, type ReactionType, type ReactionCounts } from '../../types/api';

interface ReactionPickerProps {
  commitId: string;
  reactionCounts: ReactionCounts;
  userReactions?: ReactionType[];
  onSignInRequired?: () => void;
}

export default function ReactionPicker({
  commitId,
  reactionCounts: initialCounts,
  userReactions: initialUserReactions = [],
  onSignInRequired,
}: ReactionPickerProps) {
  const { counts, userReactions, toggleReaction } = useReactions(
    commitId,
    initialCounts,
    initialUserReactions
  );

  // Memoize computed values to prevent recalculation on every render
  const { activeReactions, totalReactions, hasUserReaction } = useMemo(() => {
    const active = REACTIONS
      .filter((r) => counts[REACTION_FIELD_MAP[r.type]] > 0)
      .sort((a, b) => counts[REACTION_FIELD_MAP[b.type]] - counts[REACTION_FIELD_MAP[a.type]]);

    const total = active.reduce(
      (sum, r) => sum + counts[REACTION_FIELD_MAP[r.type]],
      0
    );

    return {
      activeReactions: active,
      totalReactions: total,
      hasUserReaction: userReactions.length > 0,
    };
  }, [counts, userReactions]);

  const handleReactionClick = (type: ReactionType) => {
    if (onSignInRequired) {
      onSignInRequired();
      return;
    }
    toggleReaction(type);
  };

  // Build the trigger content
  const renderTriggerContent = () => {
    if (activeReactions.length === 0) {
      // No reactions yet - show add reaction icon
      return (
        <span className="flex items-center gap-1.5">
          <SmilePlus className="h-4 w-4" />
        </span>
      );
    }

    // Show stacked emojis with total count
    return (
      <span
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-full text-sm',
          hasUserReaction
            ? 'bg-[hsl(var(--primary))]/10'
            : 'bg-[hsl(var(--muted))]/50'
        )}
      >
        <span className="flex items-center -space-x-1">
          {activeReactions.slice(0, 3).map((r) => (
            <span key={r.type} className="text-sm">{r.emoji}</span>
          ))}
        </span>
        <span className="text-xs font-mono font-semibold">{formatCompactNumber(totalReactions)}</span>
      </span>
    );
  };

  return (
    <DropdownMenu modal={false}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              {renderTriggerContent()}
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>React</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" className="p-2">
        <div className="flex gap-1">
          {REACTIONS.map((reaction, index) => {
            const count = counts[REACTION_FIELD_MAP[reaction.type]];
            const isActive = userReactions.includes(reaction.type);
            return (
              <Tooltip key={reaction.type}>
                <TooltipTrigger asChild>
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03, duration: 0.15 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleReactionClick(reaction.type)}
                    className={cn(
                      'flex flex-col items-center gap-0.5 px-3 py-2 rounded-md transition-colors hover:bg-[hsl(var(--muted))]',
                      isActive && 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/25'
                    )}
                  >
                    <span className="text-xl">{reaction.emoji}</span>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={count}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        transition={{ duration: 0.15 }}
                        className="text-xs font-mono font-semibold"
                      >
                        {formatCompactNumber(count)}
                      </motion.span>
                    </AnimatePresence>
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{reaction.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
